import {
  IInvoiceAdjustmentInvoiceData,
  IInvoiceAdjustmentInvoiceResult,
  IInvoiceAdjustmentRequest,
} from "@/api_framework/api_modals/payment";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import StatusBadge from "@/app/components/ui/Badges/StatusBadge";
import TableComponent, { TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { formatAmount } from "@/app/components/routes/auth_routes/SlotsScreen/ComfirmationComponent/utils/confirmationHelpers";
import { useInvoiceAdjustmentRequestDetailsAPI } from "@/app/hooks/api_hooks/payment/useInvoiceAdjustmentRequestDetailsAPI";
import { useUtils } from "@/app/hooks/useUtils";
import React, { useEffect, useMemo, useState } from "react";
import { FINISHED_STATUSES, REQUEST_TYPE_META, STATUS_META } from "../helpers";

type Props = {
  requestId: string | null;
  onClose: () => void;
};

const RequestDetailsModalComponent: React.FC<Props> = ({ requestId, onClose }) => {
  const { execute: fetchRequestDetails } = useInvoiceAdjustmentRequestDetailsAPI();
  const { veticDateFormat } = useUtils();
  const [request, setRequest] = useState<IInvoiceAdjustmentRequest | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!requestId) return;
    setRequest(null);
    setIsLoading(true);
    fetchRequestDetails(requestId).then(({ message }) => {
      setRequest(message);
      setIsLoading(false);
    });
  }, [requestId, fetchRequestDetails]);

  const hasRun = request ? FINISHED_STATUSES.includes(request.status) : false;

  // Requests raised before the switch to invoice_id are keyed by invoice_number
  const rowKey = (row: { invoice_id?: string; invoice_number?: string }) => row.invoice_id || row.invoice_number || "";
  const resultByInvoice = useMemo(() => {
    const results = new Map<string, IInvoiceAdjustmentInvoiceResult>();
    (request?.summary_dump ?? []).forEach((entry) => {
      if (typeof entry === "object" && rowKey(entry)) results.set(rowKey(entry), entry);
    });
    return results;
  }, [request]);

  const amountClosed = (request?.invoice_data ?? []).reduce((total, row) => total + (row.amount_closed || 0), 0);

  const tableRows = useMemo<IInvoiceAdjustmentInvoiceData[]>(() => {
    const processed = request?.invoice_data ?? [];
    const skipped = Array.from(resultByInvoice.values())
      .filter((result) => result.status === "INVALID")
      .map((result) => ({ invoice_id: result.invoice_id, invoice_number: result.invoice_number, amount_closed: 0 }));
    return [...processed, ...skipped];
  }, [request, resultByInvoice]);

  const infoItems = request
    ? [
        { label: "Uploaded By", value: request.raised_by_email || request.raised_by },
        { label: "Uploaded On", value: veticDateFormat(request.raised_at) ?? "-" },
        { label: "Invoices", value: request.total_invoices },
        { label: "Total Due at Upload", value: formatAmount(request.total_due_amount) },
        ...(hasRun
          ? [
              { label: "Amount Closed", value: formatAmount(amountClosed) },
              { label: "Success / Failed", value: `${request.success_count} / ${request.failed_count}` },
              { label: "Invalid (Skipped)", value: request.invalid_count ?? 0 },
            ]
          : []),
      ]
    : [];

  const columns: TableColumn<IInvoiceAdjustmentInvoiceData>[] = [
    {
      id: "invoice_id",
      header: "Invoice ID",
      cell: ({ row }) => <span className="font-mono text-[11px] text-gray-900 break-all">{rowKey(row)}</span>,
    },
    {
      id: "invoice_number",
      header: "Invoice Number",
      cell: ({ row }) => (
        <span className="whitespace-nowrap">
          {row.invoice_number || resultByInvoice.get(rowKey(row))?.invoice_number || "-"}
        </span>
      ),
    },
    {
      id: "amount_closed",
      header: hasRun ? "Amount Closed" : "Due at Upload",
      width: 130,
      cell: ({ row }) => <span className="whitespace-nowrap">{formatAmount(row.amount_closed)}</span>,
    },
    {
      id: "result",
      header: "Result",
      hidden: !hasRun,
      cell: ({ row }) => {
        const result = resultByInvoice.get(rowKey(row));
        const closed = result ? result.status === "SUCCESS" : (row.amount_closed || 0) > 0;
        const badge =
          result?.status === "INVALID"
            ? { label: "Skipped", colorClassName: "bg-orange-50 text-orange-700", fillClassName: "fill-orange-500" }
            : closed
              ? { label: "Closed", colorClassName: "bg-green-50 text-green-700", fillClassName: "fill-green-500" }
              : { label: "Not closed", colorClassName: "bg-red-50 text-red-700", fillClassName: "fill-red-500" };
        return (
          <div className="whitespace-normal">
            <StatusBadge
              label={badge.label}
              colorClassName={badge.colorClassName}
              statusFillClassname={badge.fillClassName}
            />
            {result?.message ? <p className="text-[11px] text-gray-500 mt-1 break-words">{result.message}</p> : null}
          </div>
        );
      },
    },
  ];

  const statusMeta = request ? STATUS_META[request.status] : null;

  return (
    <ModalPrompt
      modalId="INVOICE_ADJUSTMENT_DETAILS_MODAL"
      title={
        <span className="flex flex-wrap items-center gap-2">
          Request Details
          {request && statusMeta ? (
            <StatusBadge
              label={statusMeta.label}
              colorClassName={statusMeta.colorClassName}
              statusFillClassname={statusMeta.fillClassName}
            />
          ) : null}
        </span>
      }
      dialogClassName="w-full sm:my-8 sm:max-w-6xl p-4 sm:p-6"
      content={
        isLoading ? (
          <div className="py-10">
            <CircleSpinnerLoader h="40" w="40" />
          </div>
        ) : !request ? (
          <p className="py-6 text-xs text-gray-500">Could not load this request.</p>
        ) : (
          <div className="mt-2 space-y-4">
            <p className="text-xs text-gray-500">
              {REQUEST_TYPE_META[request.request_type]?.title ?? request.request_type} ·{" "}
              <span className="font-mono break-all">{request.request_id}</span>
            </p>

            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 rounded-xl border border-gray-200 bg-[#F9FAFB] p-4">
              {infoItems.map((item) => (
                <div key={item.label} className="min-w-0">
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{item.label}</dt>
                  <dd className="mt-0.5 text-sm font-medium text-gray-900 break-words">{item.value}</dd>
                </div>
              ))}
            </dl>

            <div>
              <p className="mb-2 text-xs font-semibold text-gray-700">Invoices</p>
              <TableComponent<IInvoiceAdjustmentInvoiceData>
                columns={columns}
                data={tableRows}
                getRowId={(row) => rowKey(row)}
                stickyHeader
                wrapCells
                scrollContainerClassName="max-h-[320px]"
              />
            </div>
          </div>
        )
      }
      button={{
        primaryButtonText: "Close",
        secondaryButtonText: "",
        primaryButtonClassname: "bg-black hover:bg-gray-800",
        primaryCallback: onClose,
        shouldHideOnClick: true,
        onCrossClick: onClose,
      }}
    />
  );
};

export default RequestDetailsModalComponent;
